if (!customElements.get('compact-product-bundle')) {
  customElements.define(
    'compact-product-bundle',
    class CompactProductBundle extends HTMLElement {
      constructor() {
        super();
        this.couponStorageKey = 'icedrop-main-product-coupon';
        this.excludedIceKeywords = ['deckel', 'ersatz', 'strohhalm', 'dichtung', 'reinigungstabletten', 'bubble clean', 'zubehör', 'zubehoer', 'bundle', 'set'];
        this.eligibleIceKeywords = ['thermo cup', 'thermocup', 'trinkflasche', 'thermo tasse', 'thermotasse', 'thermo shaker', 'thermoshaker', 'protein shaker', 'cup', 'bottle', 'shaker', 'tasse'];
        this.icedropCouponTiers = { oneToTwo: 20, threeToFour: 25, fivePlus: 30 };
        this.activeCartPercentageDiscounts = [];
        this.activeIceProductDiscountPercentage = 0;
        this.activeIceCodeApplied = false;
        this.cartIceEligibleQuantity = 0;
    
        this.submitButton.addEventListener('click', this.onSubmitHandler.bind(this));
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onCartUpdate = this.onCartUpdate.bind(this);
        this.onDiscountSubmit = this.onDiscountSubmit.bind(this);
        this.onDiscountRemove = this.onDiscountRemove.bind(this);
        this.onBundleToggleClick = this.onBundleToggleClick.bind(this);
        this.addEventListener('change', this.onVariantChange);
        this.addEventListener('click', this.onBundleToggleClick);
        this.variantChangeUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
        document.addEventListener('submit', this.onDiscountSubmit, true);
        document.addEventListener('click', this.onDiscountRemove, true);
        this.updateButtonLabel();
        this.syncDiscountStateFromCart();
      }
    
      get variants() {
        return Array.from(this.querySelectorAll('[data-bundle-item]'))
          .filter((item) => item.querySelector('[data-bundle-checkbox]')?.checked)
          .map((item) => item.querySelector('form[is="product-bundle-form"] [name="id"]'))
          .filter(Boolean);
      }
    
      get submitButton() {
        return this.querySelector('[data-product-bundle-submit]');
      }

      get submitButtonPrice() {
        return this.querySelector('[data-product-bundle-button-price]');
      }

      get submitButtonSeparator() {
        return this.querySelector('[data-product-bundle-button-separator]');
      }

      get submitButtonComparePrice() {
        return this.querySelector('[data-product-bundle-button-compare-price]');
      }
    
      get cartDrawer() {
        return document.querySelector('cart-drawer');
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onVariantChange);
        this.removeEventListener('click', this.onBundleToggleClick);
        this.variantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        document.removeEventListener('submit', this.onDiscountSubmit, true);
        document.removeEventListener('click', this.onDiscountRemove, true);
      }

      onVariantChange(event) {
        this.updateVariantFromSelect(event?.target);
        this.updateButtonLabel();
      }

      onBundleToggleClick(event) {
        const toggleTarget = event.target instanceof Element ? event.target.closest('[data-bundle-toggle]') : null;
        if (!toggleTarget || !this.contains(toggleTarget)) return;

        event.preventDefault();
        const card = toggleTarget.closest('[data-bundle-item]');
        const checkbox = card?.querySelector('[data-bundle-checkbox]');
        if (!checkbox) return;

        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }

      updateVariantFromSelect(target) {
        const select = target instanceof Element ? target.closest('select[name^="options["]') : null;
        if (!select || !this.contains(select)) return;

        const form = select.closest('form[is="product-bundle-form"]');
        const card = select.closest('[data-bundle-item]');
        const variantPicker = select.closest('variant-picker');
        if (!form || !card || !variantPicker) return;

        const variantsScript = variantPicker.querySelector('[data-variants]');
        if (!variantsScript?.textContent) return;

        let variants = [];
        try {
          variants = JSON.parse(variantsScript.textContent);
        } catch (error) {
          return;
        }

        const selectedOptions = Array.from(variantPicker.querySelectorAll('select[name^="options["]')).map((optionSelect) => optionSelect.value);
        const selectedVariant = variants.find((variant) => {
          return Array.isArray(variant.options) && selectedOptions.every((value, index) => String(variant.options[index]) === String(value));
        });
        if (!selectedVariant) return;

        const variantInput = form.querySelector('[name="id"]');
        if (variantInput) variantInput.value = selectedVariant.id;

        const selectedVariantScript = variantPicker.querySelector('[data-selected-variant]');
        if (selectedVariantScript) selectedVariantScript.textContent = JSON.stringify(selectedVariant);

        selectedOptions.forEach((value, index) => {
          const labelValue = variantPicker.querySelector(`#${CSS.escape(card.querySelector('product-card-info')?.dataset.sectionId || '')}-${CSS.escape(card.querySelector('product-card-info')?.dataset.productId || '')}-option${index + 1}`);
          if (labelValue) labelValue.textContent = value;
        });

        this.updateCardVariantState(card, selectedVariant, variantInput);
      }

      onCartUpdate(event) {
        if (event?.source === 'cart-discount' || event?.source === 'cart-items') {
          this.delayedSyncDiscountStateFromCart();
          return;
        }
        if (event?.cart) {
          this.updateCartDiscountState(event.cart);
          return;
        }
        this.delayedSyncDiscountStateFromCart();
      }

      onDiscountSubmit(event) {
        const formTarget = event.target instanceof Element ? event.target.closest('form[is="cart-discount"]') : null;
        if (!formTarget) return;
        this.delayedSyncDiscountStateFromCart(450);
      }

      onDiscountRemove(event) {
        const target = event.target instanceof Element ? event.target.closest('button[is="discount-remove"]') : null;
        if (!target) return;
        const removedCode = this.normalizeDiscountCode(target.getAttribute('data-discount'));
        const storedCode = this.normalizeDiscountCode(this.readStoredCoupon()?.code);
        if (removedCode && storedCode && removedCode === storedCode) {
          this.clearStoredCoupon();
        }
        this.delayedSyncDiscountStateFromCart(450);
      }

      parseDiscountPercentage(value) {
        const normalized = String(value || '').replace('%', '').replace(',', '.').trim();
        const parsed = Number.parseFloat(normalized);
        return Number.isFinite(parsed) ? parsed : 0;
      }

      formatMoney(cents) {
        const safeCents = Number.isFinite(Number(cents)) ? Number(cents) : 0;
        const format = window.themeVariables?.settings?.moneyFormat;
        if (format) {
          const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
          if (match) {
            const formatter = (number, precision = 2, thousands = '.', decimal = ',') => {
              const rounded = (Number(number) / 100).toFixed(precision);
              const parts = rounded.split('.');
              const amount = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
              return parts[1] ? amount + decimal + parts[1] : amount;
            };
            const value = match[1] === 'amount_no_decimals'
              ? formatter(safeCents, 0, '.', ',')
              : formatter(safeCents, 2, '.', ',');
            return format.replace(match[0], value);
          }
        }

        return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(safeCents / 100);
      }

      normalizeDiscountCode(value) {
        if (window.IcedropDiscountState?.normalizeCode) {
          return window.IcedropDiscountState.normalizeCode(value);
        }
        return String(value || '').trim().toLowerCase();
      }

      getApplicableDiscountCodes(cartState) {
        if (!Array.isArray(cartState?.discount_codes)) return [];
        return cartState.discount_codes
          .filter((discount) => discount?.applicable)
          .map((discount) => String(discount.code || '').trim())
          .filter(Boolean);
      }

      getLineLevelDiscountAllocations(item) {
        if (Array.isArray(item?.line_level_discount_allocations)) return item.line_level_discount_allocations;
        if (Array.isArray(item?.discounts)) return item.discounts;
        return [];
      }

      getAllocationCode(allocation) {
        return String(allocation?.discount_application?.title || allocation?.title || allocation?.code || '').trim();
      }

      getAllocationPercentage(allocation) {
        const discountApplication = allocation?.discount_application || allocation || {};
        return this.parseDiscountPercentage(
          discountApplication?.value?.percentage
            || discountApplication?.value
            || discountApplication?.percentage
            || ''
        );
      }

      getItemHandle(item) {
        const explicitHandle = String(item?.handle || item?.product_handle || '').trim().toLowerCase();
        if (explicitHandle) return explicitHandle;
        const url = String(item?.url || '').trim();
        const match = url.match(/\/products\/([^/?#]+)/i);
        return match?.[1] ? match[1].toLowerCase() : '';
      }

      getItemTitle(item) {
        return String(item?.product_title || item?.title || '').trim().toLowerCase();
      }

      isIceEligibleHandleTitle(handle, title) {
        if (!handle && !title) return false;
        if (this.excludedIceKeywords.some((keyword) => handle.includes(keyword) || title.includes(keyword))) return false;
        return this.eligibleIceKeywords.some((keyword) => handle.includes(keyword) || title.includes(keyword));
      }

      isIceEligibleItem(item) {
        return this.isIceEligibleHandleTitle(this.getItemHandle(item), this.getItemTitle(item));
      }

      isIceEligibleCard(card) {
        if (window.IcedropDiscountState?.isIceEligibleCard) {
          return window.IcedropDiscountState.isIceEligibleCard(card);
        }
        const handle = String(card.getAttribute('data-product-handle') || '').trim().toLowerCase();
        const title = String(card.getAttribute('data-product-title') || '').trim().toLowerCase();
        return this.isIceEligibleHandleTitle(handle, title);
      }

      getIceTierPercentage(eligibleQuantity) {
        if (eligibleQuantity >= 5) return this.icedropCouponTiers.fivePlus;
        if (eligibleQuantity >= 3) return this.icedropCouponTiers.threeToFour;
        if (eligibleQuantity >= 1) return this.icedropCouponTiers.oneToTwo;
        return 0;
      }

      readStoredCoupon() {
        try {
          const sharedCoupon = window.IcedropDiscountState?.readStoredCoupon?.();
          if (sharedCoupon) return sharedCoupon;

          const raw = window.sessionStorage.getItem(this.couponStorageKey);
          return raw ? JSON.parse(raw) : null;
        } catch (error) {
          return null;
        }
      }

      clearStoredCoupon() {
        try {
          window.IcedropDiscountState?.clearStoredCoupon?.();
          if (!window.IcedropDiscountState?.clearStoredCoupon) {
            window.sessionStorage.removeItem(this.couponStorageKey);
          }
        } catch (error) {
        }
      }

      getCartPercentageDiscounts(cartState) {
        if (window.IcedropDiscountState?.getCartPercentageDiscounts) {
          return window.IcedropDiscountState.getCartPercentageDiscounts(cartState);
        }

        const applicableCodes = this.getApplicableDiscountCodes(cartState).map((code) => this.normalizeDiscountCode(code));
        const applications = Array.isArray(cartState?.cart_level_discount_applications) ? cartState.cart_level_discount_applications : [];
        const discountsByCode = new Map();

        applications.forEach((application) => {
          if (application?.type !== 'discount_code' || application?.value_type !== 'percentage') return;
          const code = String(application.title || '').trim();
          if (!code) return;
          if (applicableCodes.length && !applicableCodes.includes(this.normalizeDiscountCode(code))) return;
          const percentage = this.parseDiscountPercentage(application.value);
          if (percentage <= 0) return;
          discountsByCode.set(this.normalizeDiscountCode(code), { code, percentage });
        });

        (Array.isArray(cartState?.items) ? cartState.items : []).forEach((item) => {
          this.getLineLevelDiscountAllocations(item).forEach((allocation) => {
            const discountApplication = allocation?.discount_application || allocation || {};
            if (discountApplication?.type && discountApplication.type !== 'discount_code') return;
            if (discountApplication?.value_type && discountApplication.value_type !== 'percentage') return;
            const code = this.getAllocationCode(allocation);
            const normalizedCode = this.normalizeDiscountCode(code);
            if (!normalizedCode || normalizedCode === 'ice') return;
            if (applicableCodes.length && !applicableCodes.includes(normalizedCode)) return;
            const percentage = this.getAllocationPercentage(allocation);
            if (percentage <= 0 || discountsByCode.has(normalizedCode)) return;
            discountsByCode.set(normalizedCode, { code, percentage });
          });
        });

        return Array.from(discountsByCode.values());
      }

      getIceProductDiscountPercentage(cartState) {
        if (window.IcedropDiscountState?.getIceProductDiscountPercentage) {
          return window.IcedropDiscountState.getIceProductDiscountPercentage(cartState, { storedCoupon: this.readStoredCoupon() });
        }

        const applicableCodes = this.getApplicableDiscountCodes(cartState).map((code) => this.normalizeDiscountCode(code));
        const hasIceApplied = applicableCodes.includes('ice');

        if (!hasIceApplied) {
          const storedCoupon = this.readStoredCoupon();
          if (this.normalizeDiscountCode(storedCoupon?.code) === 'ice' && (storedCoupon?.status === 'pending' || storedCoupon?.status === 'applied')) {
            return this.getIceTierPercentage(1);
          }
          return 0;
        }

        const matchedPercentages = (Array.isArray(cartState?.items) ? cartState.items : []).flatMap((item) => {
          return this.getLineLevelDiscountAllocations(item)
            .filter((allocation) => this.normalizeDiscountCode(this.getAllocationCode(allocation)) === 'ice')
            .map((allocation) => this.getAllocationPercentage(allocation))
            .filter((value) => value > 0);
        });

        if (matchedPercentages.length > 0) {
          return Math.max(...matchedPercentages);
        }

        const eligibleQuantity = (Array.isArray(cartState?.items) ? cartState.items : []).reduce((sum, item) => {
          return sum + (this.isIceEligibleItem(item) ? Number(item.quantity) || 0 : 0);
        }, 0);

        return this.getIceTierPercentage(eligibleQuantity || 1);
      }

      getSelectedIceEligibleQuantity() {
        return Array.from(this.querySelectorAll('[data-bundle-item]')).reduce((sum, card) => {
          if (!card.querySelector('[data-bundle-checkbox]')?.checked) return sum;
          return sum + (this.isIceEligibleCard(card) ? 1 : 0);
        }, 0);
      }

      getEffectiveIceProductDiscountPercentage() {
        if (!this.activeIceCodeApplied && this.activeIceProductDiscountPercentage <= 0) return 0;
        return this.activeIceProductDiscountPercentage;
      }

      getCombinedOrderDiscountMultiplier() {
        if (window.IcedropDiscountState?.getCombinedMultiplier) {
          return window.IcedropDiscountState.getCombinedMultiplier(this.activeCartPercentageDiscounts);
        }

        const combinedCodes = [];
        const seenCodes = new Set();

        this.activeCartPercentageDiscounts.forEach((discount) => {
          const normalizedCode = this.normalizeDiscountCode(discount?.code);
          if (!normalizedCode || seenCodes.has(normalizedCode)) return;
          seenCodes.add(normalizedCode);
          combinedCodes.push({ code: String(discount.code || '').trim(), percentage: this.parseDiscountPercentage(discount.percentage) });
        });

        return combinedCodes.reduce((multiplier, discount) => {
          const percentage = this.parseDiscountPercentage(discount.percentage);
          if (percentage <= 0) return multiplier;
          return multiplier * ((100 - percentage) / 100);
        }, 1);
      }

      getCardPriceState(card) {
        const basePrice = Number(card.getAttribute('data-price')) || 0;
        const compareAt = Number(card.getAttribute('data-compare-price')) || 0;
        const showComparePrice = card.getAttribute('data-show-compare-price') !== 'false';
        const orderDiscountMultiplier = this.getCombinedOrderDiscountMultiplier();
        const effectiveIceProductDiscountPercentage = this.getEffectiveIceProductDiscountPercentage();
        const productDiscountMultiplier = this.isIceEligibleCard(card) && effectiveIceProductDiscountPercentage > 0
          ? (100 - effectiveIceProductDiscountPercentage) / 100
          : 1;
        const multiplier = orderDiscountMultiplier * productDiscountMultiplier;
        const discountedPrice = multiplier < 1 ? Math.max(0, Math.round(basePrice * multiplier)) : basePrice;
        const hasDiscountPreview = discountedPrice < basePrice;
        const effectiveCompareAt = hasDiscountPreview ? basePrice : compareAt;
        const onSale = effectiveCompareAt > discountedPrice;
        const showEffectiveComparePrice = hasDiscountPreview ? true : showComparePrice;
        const renderedCompareAt = showEffectiveComparePrice && onSale ? effectiveCompareAt : 0;

        return {
          basePrice,
          discountedPrice,
          compareAt: renderedCompareAt,
          isDiscounted: renderedCompareAt > discountedPrice
        };
      }

      renderCardPrice(card) {
        const priceRow = card.querySelector('[data-icedrop-price-row]');
        if (!priceRow) return;

        const priceState = this.getCardPriceState(card);
        priceRow.innerHTML = `
          <span class="icedrop-main-collection__price-current${priceState.isDiscounted ? ' is-discounted' : ''}">${this.formatMoney(priceState.discountedPrice)}</span>
          ${priceState.compareAt > priceState.discountedPrice ? `<s>${this.formatMoney(priceState.compareAt)}</s>` : ''}
        `;
      }

      updateCardPrices() {
        this.querySelectorAll('[data-bundle-item]').forEach((card) => this.renderCardPrice(card));
      }

      updateCardMedia(card, variant) {
        if (!card || !variant) return;
        const image = variant.featured_image || variant.image;
        const imageUrl = image?.src || image?.url;
        if (!imageUrl) return;

        const mediaImage = card.querySelector('.icedrop-main-collection__card-media img');
        if (!mediaImage) return;

        const normalizedUrl = imageUrl.startsWith('//') ? `${window.location.protocol}${imageUrl}` : imageUrl;
        const resizedUrl = new URL(normalizedUrl, window.location.href);
        mediaImage.srcset = [240, 360, 480, 600, 900, 1200].map((width) => {
          resizedUrl.searchParams.set('width', width);
          return `${resizedUrl.href} ${width}w`;
        }).join(', ');
        mediaImage.src = resizedUrl.href;
        if (image?.alt) mediaImage.alt = image.alt;

        const mediaLink = card.querySelector('.icedrop-main-collection__card-media');
        if (mediaLink && variant.url) mediaLink.href = variant.url;
        const titleLink = card.querySelector('[data-product-bundle-title]');
        if (titleLink && variant.url) titleLink.href = variant.url;
      }

      updateCardVariantMeta(card, variant) {
        if (!card || !variant || !Array.isArray(variant.options)) return;

        const meta = card.querySelector('[data-bundle-variant-meta]');
        if (!meta) return;

        const sizeIndex = Number.parseInt(card.getAttribute('data-size-option-index') || '-1', 10);
        const colorIndex = Number.parseInt(card.getAttribute('data-color-option-index') || '-1', 10);
        const sizeValue = sizeIndex >= 0 ? String(variant.options[sizeIndex] || '').trim() : '';
        let colorValue = colorIndex >= 0 ? String(variant.options[colorIndex] || '').trim() : '';

        if (colorValue === sizeValue) colorValue = '';

        const sizeNode = meta.querySelector('[data-bundle-variant-size]');
        const colorNode = meta.querySelector('[data-bundle-variant-color]');
        const separatorNode = meta.querySelector('[data-bundle-variant-separator]');

        if (sizeNode) {
          sizeNode.textContent = sizeValue;
          sizeNode.hidden = !sizeValue;
        }
        if (colorNode) {
          colorNode.textContent = colorValue;
          colorNode.hidden = !colorValue;
        }
        if (separatorNode) {
          separatorNode.hidden = !sizeValue || !colorValue;
        }

        meta.hidden = !sizeValue && !colorValue;
      }

      updateCardVariantState(card, variant, input) {
        if (!card || !variant) return;

        card.setAttribute('data-price', variant.price || 0);
        card.setAttribute('data-compare-price', variant.compare_at_price || 0);
        card.setAttribute('data-variant-id', variant.id || input?.value || '');
        card.setAttribute('data-available', variant.available !== false ? 'true' : 'false');
        this.updateCardMedia(card, variant);
        this.updateCardVariantMeta(card, variant);
        this.renderCardPrice(card);
      }

      updateCartDiscountState(cartState) {
        window.IcedropDiscountState?.setCartState?.(cartState);
        const discountState = window.IcedropDiscountState?.getState
          ? window.IcedropDiscountState.getState(cartState, { storedCoupon: this.readStoredCoupon() })
          : null;
        const applicableCodes = discountState?.applicableCodes?.map((code) => this.normalizeDiscountCode(code))
          || this.getApplicableDiscountCodes(cartState).map((code) => this.normalizeDiscountCode(code));
        const storedCoupon = this.readStoredCoupon();
        this.activeIceCodeApplied = applicableCodes.includes('ice') || (
          this.normalizeDiscountCode(storedCoupon?.code) === 'ice'
          && (storedCoupon?.status === 'pending' || storedCoupon?.status === 'applied')
        );
        this.cartIceEligibleQuantity = (Array.isArray(cartState?.items) ? cartState.items : []).reduce((sum, item) => {
          return sum + (this.isIceEligibleItem(item) ? Number(item.quantity) || 0 : 0);
        }, 0);
        this.activeCartPercentageDiscounts = discountState?.orderPercentageDiscounts || this.getCartPercentageDiscounts(cartState);
        this.activeIceProductDiscountPercentage = typeof discountState?.iceProductPercentage === 'number'
          ? discountState.iceProductPercentage
          : this.getIceProductDiscountPercentage(cartState);
        this.updateCardPrices();
        this.updateButtonLabel();
      }

      async syncDiscountStateFromCart(options = {}) {
        try {
          const cachedCartState = window.IcedropDiscountState?.getCartState
            ? await window.IcedropDiscountState.getCartState({ force: Boolean(options.force) })
            : null;
          let cartState = cachedCartState;
          if (!cartState) {
            const response = await fetch(`${theme.routes.cart_url}.js`, { credentials: 'same-origin' });
            if (!response.ok) return;
            cartState = await response.json();
          }
          this.updateCartDiscountState(cartState);
        } catch (error) {
          console.warn('[compact-product-bundle] discount sync failed', error);
        }
      }

      async delayedSyncDiscountStateFromCart(delay = 180) {
        await new Promise((resolve) => window.setTimeout(resolve, delay));
        await this.syncDiscountStateFromCart({ force: true });
      }

      getCurrentCartItemCount() {
        const countElement = document.querySelector('cart-count');
        const count = Number(countElement?.textContent?.trim());
        return Number.isFinite(count) ? count : 0;
      }

      getAddedItemCount(data) {
        return (data.items || []).reduce((sum, item) => sum + (Number(item.quantity) || 1), 0);
      }

      buildImmediateCartState(parsedState, addedItemCount) {
        const itemCount = Number(parsedState?.item_count);
        return {
          ...parsedState,
          item_count: Number.isFinite(itemCount) ? itemCount : this.getCurrentCartItemCount() + addedItemCount
        };
      }

      refreshCartStateAfterAdd(parsedState) {
        const cartStatePromise = window.IcedropDiscountState?.getCartState
          ? window.IcedropDiscountState.getCartState({ force: true })
          : fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET')}).then((response) => response.json());

        cartStatePromise
          .then((cartJson) => {
            cartJson.sections = parsedState.sections;
            window.IcedropDiscountState?.setCartState?.(cartJson);
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-bundle', cart: cartJson });
            document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
              detail: {
                cart: cartJson,
                product: parsedState
              }
            }));
          })
          .catch((error) => {
            console.warn('[compact-product-bundle] cart refresh after add failed', error);
          });
      }

      updateButtonLabel() {
        if (!this.submitButtonPrice) return;

        this.querySelectorAll('[data-bundle-item]').forEach((item) => {
          item.classList.toggle('unchecked', !item.querySelector('[data-bundle-checkbox]')?.checked);
          const input = item.querySelector('form[is="product-bundle-form"] [name="id"]');
          const form = input?.closest('form[is="product-bundle-form"]');
          const variant = typeof form?.getSelectedVariant === 'function' ? form.getSelectedVariant() : null;
          this.updateCardVariantState(item, variant, input);
        });

        const totals = this.variants.reduce((sum, input) => {
          const form = input.closest('form[is="product-bundle-form"]');
          if (!form || typeof form.getSelectedVariant !== 'function') return sum;

          const variant = form.getSelectedVariant();
          if (!variant || typeof variant.price !== 'number') return sum;

          const card = input.closest('[data-bundle-item]');
          const priceState = card ? this.getCardPriceState(card) : { basePrice: variant.price, discountedPrice: variant.price, isDiscounted: false };

          sum.base += priceState.basePrice;
          sum.discounted += priceState.discountedPrice;
          return sum;
        }, { base: 0, discounted: 0 });

        this.submitButtonPrice.textContent = totals.discounted > 0 ? this.formatMoney(totals.discounted) : '';
        if (this.submitButtonComparePrice) {
          this.submitButtonComparePrice.textContent = totals.discounted > 0 && totals.discounted < totals.base ? this.formatMoney(totals.base) : '';
          this.submitButtonComparePrice.toggleAttribute('hidden', totals.discounted <= 0 || totals.discounted >= totals.base);
        }
        this.submitButtonSeparator?.toggleAttribute('hidden', totals.discounted <= 0);
        this.submitButton.toggleAttribute('disabled', this.variants.length === 0);
      }
    
      onSubmitHandler(event) {
        const data = {
          items: this.variants.map(variant => ({
            id: variant.value,
            quantity: 1
          }))
        };
    
        if (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page') {
          theme.utils.postLink2(theme.routes.cart_add_url, {
            parameters: {
              ...data
            }
          });
          return;
        }
        
        event.preventDefault();
        if (this.submitButton.hasAttribute('aria-disabled')) return;
        this.activeElement = event.submitter || event.currentTarget;
    
        this.handleErrorMessage();
    
        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));
        
        const body = JSON.stringify({
          ...data,
          sections: sectionsToBundle,
          sections_url: window.location.pathname
        });
    
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.setAttribute('aria-busy', 'true');
    
        fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then(async (parsedState) => {
            if (parsedState.status) {
              this.handleErrorMessage(parsedState.description);
              document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
                detail: {
                  errorMessage: parsedState.description
                }
              }));
              
              const submitButtonText = this.submitButton.querySelector('.btn-text span');
              if (!submitButtonText || !submitButtonText.hasAttribute('data-sold-out')) return;
              submitButtonText.innerText = submitButtonText.getAttribute('data-sold-out');
              this.submitButton.setAttribute('aria-disabled', 'true');
              this.error = true;
              return;
            }
    
            const immediateCartState = this.buildImmediateCartState(parsedState, this.getAddedItemCount(data));
            this.cartDrawer?.show(this.activeElement);
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-bundle', cart: immediateCartState });
            this.refreshCartStateAfterAdd(parsedState);
          })
          .catch((error) => {
            console.log(error);
          })
          .finally(() => {
            this.submitButton.removeAttribute('aria-busy');
    
            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
            }
          });
      }
    
      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;
        
        this.errorMessage = this.errorMessage || this.querySelector('.product-form__error-message');
        if (!this.errorMessage) return;
    
        this.errorMessage.toggleAttribute('hidden', !errorMessage);
        this.errorMessage.innerText = errorMessage;
      }
    }
  );
}
